import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const shimmerEffect = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const Card = styled(motion.div)`
  background: linear-gradient(
    165deg,
    rgba(25, 25, 25, 0.95),
    rgba(35, 35, 35, 0.98)
  );
  border-radius: 20px;
  padding: 24px;
  width: 100%;
  min-width: 340px;
  max-width: 380px;
  margin: 16px auto;
  cursor: pointer;
  color: #fff;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 180, 219, 0.2);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.4);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at top right,
      rgba(0, 180, 219, 0.15),
      transparent 60%
    );
    z-index: 0;
    opacity: 0.5;
  }
`;

const CardTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 2px 10px rgba(0, 180, 219, 0.5);
  background: none;
  -webkit-background-clip: unset;
  -webkit-text-fill-color: unset;
  position: relative;
  z-index: 1;

  span {
    font-size: 1.2em;
    filter: none;
  }
`;

const Thumbnail = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(45deg, #2a2a2a, #3a3a3a);
  border-radius: 10px;
  margin: 16px 0;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: ${shimmerEffect} 2s infinite;
  }

  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: all 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 180, 219, 0.1) 0%,
      transparent 50%,
      rgba(0, 131, 176, 0.05) 100%
    );
    border: 1px solid rgba(0, 180, 219, 0.3);
    border-radius: 10px;
    opacity: 0.3;
    pointer-events: none;
  }

  ${Card}:hover & img {
    transform: scale(1.02);
  }
`;

const BuyButton = styled(Link)`
  display: block;
  background: linear-gradient(135deg, #00b4db, #0083b0);
  color: white;
  padding: 12px 24px;
  border-radius: 30px;
  text-align: center;
  text-decoration: none;
  font-weight: bold;
  margin-top: 16px;
  transition: transform 0.2s;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 180, 219, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 180, 219, 0.4);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

interface ShortcutCardProps {
  title: string;
  icon: string;
  thumbnail: string;
  buyLink: string;
}

export const ShortcutCard = ({ title, icon, thumbnail, buyLink }: ShortcutCardProps) => {
  return (
    <Card
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <CardTitle><span>{icon}</span> {title}</CardTitle>
      <Thumbnail>
        <img src={thumbnail} alt={title} />
      </Thumbnail>
      <BuyButton to={buyLink}>
        Buy Now
      </BuyButton>
    </Card>
  );
};
